import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";

import CustomDialog from "../../../components/modals/CustomDialog";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectAuth } from "../../auth/authSlice";
import { selectPreference } from "../../preferences/preferencesSlice";
import { isPrivilegedTransaction } from "../../../utils/authUtils";
import { Role } from "../../auth/types/authType";
import { RoleTypes } from "../../auth/types/roleTypes";
import { Employee, EmployeeStatus, MedicalStatus } from "../types/processTypes";
import { bulkUpdateEmployeeStatus } from "../processSlices";

/** Relevant next statuses offered for a bulk update, scoped to the active tab. */
const BULK_TAB_STATUS_OPTIONS: Record<string, EmployeeStatus[]> = {
  pendingInfo: [EmployeeStatus.new],
  available: [],
  selected: [EmployeeStatus.contracted],
  contracted: [EmployeeStatus.visa_issued, EmployeeStatus.labour_issued],
  embassyProcess: [EmployeeStatus.visa_issued],
  lmisProcess: [EmployeeStatus.labour_issued],
  ticketing: [EmployeeStatus.flight_booked],
  flightBooked: [EmployeeStatus.departed],
  departed: [
    EmployeeStatus.contract_completed,
    EmployeeStatus.returned,
    EmployeeStatus.lost,
    EmployeeStatus.has_complain,
    EmployeeStatus.other,
  ],
  discontinuedStatuses: [EmployeeStatus.new],
  stoppedStatuses: [EmployeeStatus.new],
};

/** Mirrors the server `STATUS_ROLE` map: target status → role required to set it. */
const STATUS_ROLE_TYPE: Partial<Record<EmployeeStatus, RoleTypes>> = {
  [EmployeeStatus.selected]: RoleTypes.SelectCandidates,
  [EmployeeStatus.contracted]: RoleTypes.ContractEmployees,
  [EmployeeStatus.visa_issued]: RoleTypes.IssueEmployeeVisa,
  [EmployeeStatus.labour_issued]: RoleTypes.IssueEmployeeLabour,
  [EmployeeStatus.flight_booked]: RoleTypes.BookEmployeeTicket,
  [EmployeeStatus.departed]: RoleTypes.MarkEmployeeDeparted,
  [EmployeeStatus.discontinued]: RoleTypes.DiscontinueProcess,
  [EmployeeStatus.stopped]: RoleTypes.StopProcess,
  [EmployeeStatus.lost]: RoleTypes.ChangeEmployeeDisposition,
  [EmployeeStatus.returned]: RoleTypes.ChangeEmployeeDisposition,
  [EmployeeStatus.has_complain]: RoleTypes.ChangeEmployeeDisposition,
  [EmployeeStatus.contract_completed]: RoleTypes.ChangeEmployeeDisposition,
  [EmployeeStatus.other]: RoleTypes.ChangeEmployeeDisposition,
};

/** Statuses a user is allowed to set, for the given active tab. `new` has no role gate. */
export function bulkStatusOptionsForTab(
  tab: string,
  roles: Role[] | undefined,
): EmployeeStatus[] {
  const r = roles ?? [];
  const manageAll = isPrivilegedTransaction(r, RoleTypes.ChangeEmployeeStatus);
  return (BULK_TAB_STATUS_OPTIONS[tab] ?? []).filter((status) => {
    if (manageAll) return true;
    const role = STATUS_ROLE_TYPE[status];
    return role == null || isPrivilegedTransaction(r, role);
  });
}

const employeeName = (e: Employee) =>
  `${e.givenName ?? ""} ${e.surName ?? ""}`.trim() || "—";

/**
 * Mirrors the per-step prerequisites enforced one-by-one in `ProcessStages`
 * (the backend only checks roles). Returns the reason a target status cannot
 * be applied to an employee, or `null` if eligible.
 */
function ineligibleReason(
  status: EmployeeStatus,
  e: Employee,
): string | null {
  switch (status) {
    case EmployeeStatus.visa_issued:
      return e.medicalStatus === MedicalStatus.fit
        ? null
        : "medical not completed";
    case EmployeeStatus.labour_issued:
      if (!e.cocCompleted) return "COC not completed";
      return e.medicalStatus === MedicalStatus.unfit ? "medically unfit" : null;
    case EmployeeStatus.flight_booked:
      if (!e.visaIssued || !e.labourIssued) return "visa/labour not issued";
      return e.medicalStatus === MedicalStatus.unfit ? "medically unfit" : null;
    case EmployeeStatus.departed:
      if (!e.ticketBooked) return "ticket not booked";
      return e.medicalStatus === MedicalStatus.unfit ? "medically unfit" : null;
    default:
      return null;
  }
}

interface Props {
  selectedEmployees: Employee[];
  activeStatusTab: string;
  onClose: (saved: boolean) => void;
}

export const BulkStatusUpdateDialog = ({
  selectedEmployees,
  activeStatusTab,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const { locale } = useAppSelector(selectPreference);

  const statusOptions = useMemo(
    () => bulkStatusOptionsForTab(activeStatusTab, user?.roles as Role[]),
    [activeStatusTab, user?.roles],
  );

  const [status, setStatus] = useState<EmployeeStatus | "">(
    statusOptions[0] ?? "",
  );
  const [date, setDate] = useState<Date | null>(new Date());
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  /** Set when some selected employees fail prerequisites: holds who is eligible/ineligible. */
  const [confirm, setConfirm] = useState<{
    eligible: Employee[];
    ineligible: { employee: Employee; reason: string }[];
  } | null>(null);

  const doUpdate = async (employees: Employee[]) => {
    if (!status || employees.length === 0) return;
    setSubmitting(true);
    const result = await dispatch(
      bulkUpdateEmployeeStatus({
        ids: employees
          .map((e) => String(e._id))
          .filter((id) => id && id !== "undefined"),
        status,
        currentStatusDate: date ?? undefined,
        currentStatusRemark: remark.trim() || undefined,
      }),
    );
    setSubmitting(false);
    if (bulkUpdateEmployeeStatus.fulfilled.match(result)) {
      onClose(true);
    }
  };

  const handleApply = () => {
    if (!status) return;
    const eligible: Employee[] = [];
    const ineligible: { employee: Employee; reason: string }[] = [];
    for (const e of selectedEmployees) {
      const reason = ineligibleReason(status, e);
      if (reason) ineligible.push({ employee: e, reason });
      else eligible.push(e);
    }
    if (ineligible.length === 0) {
      void doUpdate(eligible);
    } else {
      setConfirm({ eligible, ineligible });
    }
  };

  return (
    <CustomDialog
      title={t("bulkStatusUpdate")}
      isOpen
      handleDialogClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {`${selectedEmployees.length} ${t("employees")}`}
        </Typography>
        <Box
          sx={{
            mb: 2,
            p: 1,
            height: 96,
            overflowY: "auto",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "action.hover",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: 1,
          }}
        >
          {selectedEmployees.map((e, i) => (
            <Typography
              key={String(e._id)}
              variant="caption"
              component="div"
              noWrap
            >
              {`${i + 1}. ${employeeName(e)}`}
            </Typography>
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("status")}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as EmployeeStatus);
                setConfirm(null);
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {te(option)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={locale}
            >
              <DatePicker
                label={t("currentStatusDate")}
                views={["year", "month", "day"]}
                minDate={new Date("2023-01-01")}
                disableMaskedInput
                inputFormat="DD-MMM-YYYY"
                value={date}
                onChange={(value) => setDate(value as Date | null)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              label={t("currentStatusRemark")}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            {confirm ? (
              <Stack spacing={1.5}>
                <Alert severity="warning">
                  {`${confirm.ineligible.length} of ${selectedEmployees.length} do not meet the requirements for "${status ? te(status) : ""}" and will be skipped.`}
                </Alert>
                <Box
                  sx={{
                    p: 1,
                    maxHeight: 120,
                    overflowY: "auto",
                    border: 1,
                    borderColor: "warning.light",
                    borderRadius: 1,
                  }}
                >
                  {confirm.ineligible.map(({ employee, reason }) => (
                    <Typography
                      key={String(employee._id)}
                      variant="caption"
                      component="div"
                      noWrap
                    >
                      {`${employeeName(employee)} — ${reason}`}
                    </Typography>
                  ))}
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled={submitting}
                    onClick={() => setConfirm(null)}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    color="secondary"
                    variant="contained"
                    fullWidth
                    disabled={confirm.eligible.length === 0 || submitting}
                    onClick={() => doUpdate(confirm.eligible)}
                  >
                    <Save />
                    {`${t("save")} (${confirm.eligible.length})`}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Button
                color="secondary"
                variant="contained"
                size="large"
                fullWidth
                disabled={!status || submitting}
                onClick={handleApply}
              >
                <Save />
                {t("save") + " " + t("status")}
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </CustomDialog>
  );
};
